import { useState, useEffect, useRef } from "react";
import { Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useBranding, useUpdateBranding, useLogoUploadUrl } from "@/hooks/use-api";

export function BrandingTab() {
  const { data, isLoading, isError } = useBranding();
  const updateMutation = useUpdateBranding();
  const logoUploadMutation = useLogoUploadUrl();

  const [companyName, setCompanyName] = useState("");
  const [primaryColor, setPrimaryColor] = useState("");
  const [secondaryColor, setSecondaryColor] = useState("");
  const [accentColor, setAccentColor] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const branding = data?.branding;

  useEffect(() => {
    if (branding) {
      setCompanyName(branding.company_name ?? "");
      setPrimaryColor(branding.primary_color ?? "");
      setSecondaryColor(branding.secondary_color ?? "");
      setAccentColor(branding.accent_color ?? "");
    }
  }, [branding]);

  async function handleSave() {
    setSaveError(null);
    setSaveSuccess(false);
    try {
      await updateMutation.mutateAsync({
        company_name: companyName || undefined,
        primary_color: primaryColor || undefined,
        secondary_color: secondaryColor || undefined,
        accent_color: accentColor || undefined,
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : "Failed to save branding",
      );
    }
  }

  async function handleLogoUpload(file: File) {
    try {
      const { upload_url, fields } = await logoUploadMutation.mutateAsync({
        content_type: file.type,
        file_size: file.size,
      });

      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append("file", file);

      await fetch(upload_url, { method: "POST", body: formData });

      // Refresh branding to get new logo_url
      await updateMutation.mutateAsync({});
    } catch {
      setSaveError("Failed to upload logo");
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="max-w-lg space-y-6">
      <h3 className="text-sm font-medium text-muted-foreground">
        White-Label Branding
      </h3>

      {isError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          Failed to load branding settings.
        </div>
      )}

      <div>
        <Label htmlFor="company-name">Company Name</Label>
        <Input
          id="company-name"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          placeholder="Your Company"
          className="mt-1.5"
        />
      </div>

      <div>
        <Label>Logo</Label>
        <div className="mt-1.5 flex items-center gap-4">
          {branding?.logo_url ? (
            <img
              src={branding.logo_url}
              alt="Company logo"
              className="h-12 w-12 rounded-lg border object-contain"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-lg border bg-muted text-xs text-muted-foreground">
              N/A
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const file = e.target.files?.[0];
              if (file) handleLogoUpload(file);
            }}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={logoUploadMutation.isPending}
          >
            {logoUploadMutation.isPending ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-1.5 h-4 w-4" />
            )}
            Upload New
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        <Label>Colors</Label>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label htmlFor="branding-primary-color" className="text-xs text-muted-foreground">
              Primary
            </label>
            <div className="mt-1 flex items-center gap-2">
              <Input
                id="branding-primary-color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                placeholder="#000000"
                className="font-mono text-xs"
              />
              {primaryColor && (
                <div
                  className="h-8 w-8 shrink-0 rounded border"
                  style={{ backgroundColor: primaryColor }}
                />
              )}
            </div>
          </div>
          <div>
            <label htmlFor="branding-secondary-color" className="text-xs text-muted-foreground">
              Secondary
            </label>
            <div className="mt-1 flex items-center gap-2">
              <Input
                id="branding-secondary-color"
                value={secondaryColor}
                onChange={(e) => setSecondaryColor(e.target.value)}
                placeholder="#000000"
                className="font-mono text-xs"
              />
              {secondaryColor && (
                <div
                  className="h-8 w-8 shrink-0 rounded border"
                  style={{ backgroundColor: secondaryColor }}
                />
              )}
            </div>
          </div>
          <div>
            <label htmlFor="branding-accent-color" className="text-xs text-muted-foreground">
              Accent
            </label>
            <div className="mt-1 flex items-center gap-2">
              <Input
                id="branding-accent-color"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                placeholder="#000000"
                className="font-mono text-xs"
              />
              {accentColor && (
                <div
                  className="h-8 w-8 shrink-0 rounded border"
                  style={{ backgroundColor: accentColor }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {saveError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          {saveError}
        </div>
      )}

      {saveSuccess && (
        <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-400">
          Branding saved successfully.
        </div>
      )}

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={updateMutation.isPending}
        >
          {updateMutation.isPending && (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          )}
          Save Changes
        </Button>
      </div>
    </div>
  );
}
